import { AlertCircle, RefreshCw, ServerCrash } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { Button } from '../../ui/button';
import type { ApiError } from '../../../api/shared/apiError';

interface ApiErrorDetail {
  label: string;
  hasError: boolean;
}

interface ServiceUnavailableInfo {
  title: string;
  detail: string;
  platformName?: string;
  retryAfter?: string;
}

interface ApiErrorPageProps {
  title?: string;
  errors?: ApiErrorDetail[];
  serviceError?: ServiceUnavailableInfo;
  onRetry?: () => void;
}

/**
 * Extracts service unavailability info from an ApiError if it is a 503
 * with RFC 7807 Problem Details.
 */
export function extractServiceError(error: unknown): ServiceUnavailableInfo | undefined {
  if (
    error != null &&
    typeof error === 'object' &&
    'status' in error &&
    (error as ApiError).status === 503 &&
    'problemDetails' in error
  ) {
    const apiError = error as ApiError;
    const pd = apiError.problemDetails;
    if (pd?.title && pd?.detail) {
      return {
        title: pd.title,
        detail: pd.detail,
        platformName: pd.platformName,
        retryAfter: pd.retryAfter,
      };
    }
  }
  return undefined;
}

export function ApiErrorPage({
  title = 'Unable to load data',
  errors = [],
  serviceError,
  onRetry,
}: Readonly<ApiErrorPageProps>) {
  const failedItems = errors.filter(e => e.hasError);

  return (
    <div className="flex items-center justify-center py-16">
      <div className="max-w-md w-full space-y-6 text-center">
        <div className="flex justify-center">
          <div className="rounded-full bg-error-100 p-4">
            {serviceError ? (
              <ServerCrash className="h-10 w-10 text-error-700" />
            ) : (
              <AlertCircle className="h-10 w-10 text-error-700" />
            )}
          </div>
        </div>

        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <div>
            <AlertTitle>{serviceError ? serviceError.title : title}</AlertTitle>
            <AlertDescription>
              {serviceError ? (
                <div className="text-left mt-2 space-y-2">
                  <p>{serviceError.detail}</p>
                  {serviceError.retryAfter && (
                    <p className="text-sm">
                      Retry after: {serviceError.retryAfter} seconds.
                    </p>
                  )}
                </div>
              ) : failedItems.length > 0 ? (
                <ul className="list-disc list-inside text-left mt-2 space-y-1">
                  {failedItems.map(item => (
                    <li key={item.label}>Failed to load {item.label}.</li>
                  ))}
                </ul>
              ) : (
                <p>An unexpected error occurred while loading the data.</p>
              )}
              {!serviceError && (
                <p className="mt-3">
                  Please check your connection and try again. If the problem
                  persists, contact support.
                </p>
              )}
            </AlertDescription>
          </div>
        </Alert>

        {onRetry && (
          <Button variant="outline" onClick={onRetry}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        )}
      </div>
    </div>
  );
}
